import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, BarChart3, Sparkles, TrendingUp, AlertTriangle,
  Layers, FlaskConical, Wrench, Cpu, Target, GitBranch, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import usePageMeta from "@/hooks/usePageMeta";

const COVERAGE_BLOCKS = [
  { key: "markets", title: "Markets", icon: Layers, accent: true },
  { key: "submarkets", title: "Submarkets", icon: Target },
  { key: "materials", title: "Materials", icon: FlaskConical },
  { key: "ingredients", title: "Ingredients", icon: FlaskConical },
  { key: "equipment", title: "Equipment", icon: Wrench },
  { key: "technologies", title: "Technologies", icon: Cpu },
  { key: "end_use_applications", title: "End-Use Applications", icon: Target },
  { key: "value_chain", title: "Value Chain", icon: GitBranch, accent: true },
];

export default function IndustryHubPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [trackers, setTrackers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/industries/${slug}`),
      api.get(`/trackers?industry_slug=${slug}`).catch(() => ({ data: [] })),
    ])
      .then(([ind, trk]) => {
        setData(ind.data);
        const allTrackers = trk.data || [];
        setTrackers(allTrackers.filter((t) => t.industry_slug === slug));
      })
      .finally(() => setLoading(false));
  }, [slug]);

  usePageMeta({
    title: data?.industry ? `${data.industry.name} Intelligence — Luminary` : "Industry · Luminary",
    description: data?.industry?.tagline || "Industry intelligence and advisory by Luminary.",
    path: `/industries/${slug}`,
    keywords: data?.industry ? `${data.industry.name}, strategic intelligence, market intelligence, advisory` : "",
  });

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-[1400px] mx-auto px-6 py-20 font-mono text-sm text-[var(--slate-light)]">LOADING…</div>
      </PublicLayout>
    );
  }
  if (!data?.industry) {
    return (
      <PublicLayout>
        <div className="max-w-[1400px] mx-auto px-6 py-20 text-[var(--navy)]">
          Industry not found.
          <Link to="/industries" className="ml-4 btn-secondary inline-flex">All industries</Link>
        </div>
      </PublicLayout>
    );
  }

  const ind = data.industry;
  const coverage = ind.coverage_detail || {};
  const hasCoverage = Object.values(coverage).some((arr) => Array.isArray(arr) && arr.length > 0);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative bg-[var(--navy)] text-white" data-testid={`industry-hero-${ind.slug}`}>
        <div className="absolute inset-0 overflow-hidden opacity-30">
          {ind.hero_image && <img src={ind.hero_image} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)] via-[var(--navy)]/80 to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <Link
            to="/industries"
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--gold)] hover:text-white inline-flex items-center gap-2"
            data-testid="industry-back"
          >
            <ArrowLeft size={12} /> All industries
          </Link>
          <h1 className="font-display font-black text-[clamp(2.6rem,7vw,5.6rem)] mt-6 leading-[0.95] tracking-tight">
            {ind.name}
          </h1>
          {ind.tagline && (
            <p className="mt-5 text-xl sm:text-2xl italic font-display text-white/85 max-w-3xl">
              {ind.tagline}
            </p>
          )}
          <div className="mt-10 grid sm:grid-cols-3 gap-6 max-w-2xl">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--gold)]">Market Size</div>
              <div className="font-display font-black text-3xl mt-2">${ind.market_size_usd_b}B</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--gold)]">CAGR</div>
              <div className="font-display font-black text-3xl mt-2 text-[var(--gold)]">{ind.cagr_pct}%</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--gold)]">Outlook</div>
              <div className="font-display text-sm sm:text-base mt-2 leading-snug text-white/85">{ind.growth_outlook}</div>
            </div>
          </div>
        </div>
      </section>

      {/* OVERVIEW + OPPORTUNITIES + RISKS */}
      <section className="border-b border-[var(--border)] bg-white py-16 sm:py-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7">
            <div className="eyebrow">Industry Overview</div>
            <p className="font-display text-xl sm:text-2xl mt-5 leading-snug text-[var(--navy)]">
              {ind.overview}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/luminous" className="btn-primary" data-testid="industry-luminous-cta">
                <Sparkles size={14} /> Generate a Luminous Brief
              </Link>
              <Link to="/trackers" className="btn-secondary">
                <BarChart3 size={14} /> Open trackers
              </Link>
              <Link to="/strategy-session" className="btn-ghost">
                Schedule Strategy Session <ChevronRight size={12} />
              </Link>
            </div>
          </div>
          <aside className="lg:col-span-5 grid grid-cols-1 gap-px bg-[var(--border)] border border-[var(--border)] self-start">
            <div className="bg-white p-6">
              <div className="eyebrow flex items-center gap-2">
                <TrendingUp size={12} className="text-[var(--gold)]" /> Key Opportunities
              </div>
              <ul className="mt-4 space-y-2.5 text-[15px]">
                {(ind.key_opportunities || []).map((o, i) => (
                  <li key={i} className="border-l-2 border-[var(--gold)] pl-3 text-[var(--navy)] leading-snug">{o}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-6">
              <div className="eyebrow flex items-center gap-2">
                <AlertTriangle size={12} className="text-[var(--slate-light)]" /> Key Risks
              </div>
              <ul className="mt-4 space-y-2.5 text-[15px]">
                {(ind.key_risks || []).map((r, i) => (
                  <li key={i} className="border-l-2 border-[var(--slate-light)] pl-3 text-[var(--slate)] leading-snug">{r}</li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* INTELLIGENCE COVERAGE — NEW */}
      {hasCoverage && (
        <section className="border-b border-[var(--border)] bg-[var(--bg-2)] py-16 sm:py-20" data-testid="intelligence-coverage">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-10 mb-12">
              <div className="lg:col-span-6">
                <div className="eyebrow">Intelligence Coverage</div>
                <h2 className="font-display font-black text-3xl sm:text-4xl mt-3 leading-tight text-[var(--navy)]">
                  Where we go deep in {ind.name}.
                </h2>
              </div>
              <div className="lg:col-span-6">
                <p className="text-[15px] text-[var(--slate)] leading-relaxed">
                  Our coverage in {ind.name} spans the full value chain — from raw materials and
                  ingredients through equipment and technologies to end-use applications. Each
                  engagement is built from this coverage map and tailored to your specific decision.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border)] border border-[var(--border)]">
              {COVERAGE_BLOCKS.map((block) => {
                const items = coverage[block.key] || [];
                if (items.length === 0) return null;
                const Icon = block.icon;
                return (
                  <div
                    key={block.key}
                    className={`p-5 sm:p-6 ${block.accent ? "bg-[var(--navy)] text-white" : "bg-white"}`}
                    data-testid={`coverage-block-${block.key}`}
                  >
                    <Icon size={16} className={block.accent ? "text-[var(--gold)]" : "text-[var(--navy)]"} />
                    <div className={`font-mono text-[10px] uppercase tracking-[0.18em] mt-3 ${block.accent ? "text-[var(--gold)]" : "text-[var(--slate-light)]"}`}>
                      {block.title}
                    </div>
                    <ul className={`mt-3 space-y-1.5 text-[13px] leading-relaxed ${block.accent ? "text-white/85" : "text-[var(--navy)]"}`}>
                      {items.map((item, i) => (
                        <li key={i} className="flex gap-2">
                          <span className={block.accent ? "text-[var(--gold)]" : "text-[var(--gold)]"}>·</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* TRACKERS FOR THIS INDUSTRY */}
      {trackers.length > 0 && (
        <section className="border-b border-[var(--border)] bg-white py-16 sm:py-20" data-testid="industry-trackers">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
              <div>
                <div className="eyebrow">Live Intelligence</div>
                <h2 className="font-display font-black text-3xl sm:text-4xl mt-3 leading-tight text-[var(--navy)]">
                  Trackers covering {ind.name}.
                </h2>
              </div>
              <Link to="/trackers" className="btn-ghost">All trackers <ArrowRight size={13} /></Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trackers.map((t) => (
                <Link
                  key={t.slug}
                  to={`/trackers/${t.slug}`}
                  className="block bg-white border border-[var(--border)] p-5 hover-lift"
                  data-testid={`industry-tracker-${t.slug}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--gold)]">
                    {t.frequency || "Tracker"}
                  </div>
                  <h3 className="font-display font-bold text-lg text-[var(--navy)] mt-2 leading-snug">
                    {t.title}
                  </h3>
                  <p className="mt-2 text-sm text-[var(--slate)] leading-relaxed">{t.blurb}</p>
                  <div className="mt-4 pt-3 border-t border-[var(--border)] text-[11px] font-mono uppercase text-[var(--navy)] flex items-center justify-between">
                    Open tracker <ArrowRight size={12} className="text-[var(--gold)]" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[var(--navy)] text-white py-16 sm:py-20">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--gold)]">Take it further</div>
          <h2 className="font-display font-black text-3xl sm:text-5xl mt-3 leading-tight">
            Bring us your {ind.name.toLowerCase()} decision.
          </h2>
          <p className="mt-5 text-white/75 max-w-2xl mx-auto leading-relaxed">
            Schedule a 60-minute strategy session with a Luminary practice lead. Scope your decision,
            see our methodology, and receive a fixed-fee proposal within one business day.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/strategy-session" className="btn-gold" data-testid="industry-bottom-cta">
              Schedule Strategy Session <ArrowRight size={14} />
            </Link>
            <Link to="/contact" className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)] hover:text-white self-center inline-flex items-center gap-2">
              Or contact the desk →
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
